import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAppId } from '../context/AppIdContext';
import '../style/speakSearch.css';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../component/header'; // Added Header import

const BASE_URL = process.env.REACT_APP_BASE_URL;

const SurnameGroup = () => {
    // --- State ---
    const [voters, setVoters] = useState([]);
    const [surnames, setSurnames] = useState([]);
    const [viewMode, setViewMode] = useState(""); // 'voters' | 'surnames' | 'none'
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // --- Group Creation State ---
    const [selectedSurnames, setSelectedSurnames] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // --- Pagination State ---
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;

    // --- Context ---
    const { appId, language, userId } = useAppId();
    const lang = language || 'en';
    const location = useLocation();
    const navigate = useNavigate();

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitleVoters: "Surname Group Voters",
            pageTitleSurnames: "All Surnames",
            filterVoters: "Filter voters by name...",
            filterSurnames: "Filter surnames...",
            loadingData: "Loading Data...",
            noData: "No records found.",
            createGroupBtn: "Create Group",
            creatingBtn: "Creating...",
            updateSurnameBtn: "Update Surname",
            fatherHusband: "F/S :",
            serialNo: "Sr No. :",
            mobile: "Mobile :",
            idCardNo: "ID Card No :",
            boothNo: "Booth No :",
            houseNo: "House No :",
            pollingLoc: "Voting Loc :",
            call: "Call",
            text: "Text",
            slip: "Slip",
            prachar: "Prachar",
            print: "Print",
            family: "Family"
        },
        hi: {
            pageTitleVoters: "उपनाम समूह मतदाता",
            pageTitleSurnames: "सभी उपनाम",
            filterVoters: "नाम से मतदाता फ़िल्टर करें...",
            filterSurnames: "उपनाम फ़िल्टर करें...",
            loadingData: "डेटा लोड हो रहा है...",
            noData: "कोई रिकॉर्ड नहीं मिला।",
            createGroupBtn: "समूह बनाएं",
            creatingBtn: "बना रहा है...",
            updateSurnameBtn: "उपनाम अपडेट करें",
            fatherHusband: "पिता/पति :",
            serialNo: "क्रमांक :",
            mobile: "मोबाइल :",
            idCardNo: "पहचान पत्र नं.:",
            boothNo: "बूथ नं :",
            houseNo: "घर नं :",
            pollingLoc: "मतदान स्थान :",
            call: "कॉल",
            text: "मैसेज",
            slip: "पर्ची",
            prachar: "प्रचार",
            print: "प्रिंट",
            family: "परिवार"
        },
        gu: {
            pageTitleVoters: "ઉપનામ જૂથ મતદારો",
            pageTitleSurnames: "બધા ઉપનામો",
            filterVoters: "મતદારોને નામથી ફિલ્ટર કરો...",
            filterSurnames: "ઉપનામો ફિલ્ટર કરો...",
            loadingData: "ડેટા લોડ થઈ રહ્યો છે...",
            noData: "કોઈ રેકોર્ડ મળ્યા નથી.",
            createGroupBtn: "જૂથ બનાવો",
            creatingBtn: "બનાવી રહ્યા છે...",
            updateSurnameBtn: "ઉપનામ અપડેટ કરો",
            fatherHusband: "પિતા/પતિ :",
            serialNo: "ક્રમાંક :",
            mobile: "મોબાઇલ :",
            idCardNo: "ઓળખ કાર્ડ નં :",
            boothNo: "બૂથ નં :",
            houseNo: "ઘર નં :",
            pollingLoc: "મતદાન સ્થળ :",
            call: "કૉલ",
            text: "સંદેશ",
            slip: "સ્લિપ",
            prachar: "પ્રચાર",
            print: "પ્રિન્ટ",
            family: "કુટુંબ"
        }
    };

    const txt = t[lang];
    const passedUserId = location.state?.userId;
    const targetUserId = passedUserId || userId;

    // --- API Fetch Logic ---
    const fetchData = async () => {
        if (!appId || !userId) return;
        setLoading(true);
        setSearchTerm("");
        setCurrentPage(1);
        setSelectedSurnames([]);

        try {
            const voterResponse = await axios.post(
                `${BASE_URL}/api/display_surname_group`,
                {
                    app_id: appId,
                    user_id: targetUserId
                }
            );

            if (voterResponse.data.success && Array.isArray(voterResponse.data.data.result) && voterResponse.data.data.result.length > 0) {
                setVoters(voterResponse.data.data.result);
                setViewMode('voters');
            } else {
                const surnameResponse = await axios.post(
                    `${BASE_URL}/api/dis_all_surname`,
                    {
                        app_id: appId,
                        user_id: userId
                    }
                );

                if (surnameResponse.data.success && Array.isArray(surnameResponse.data.data.result)) {
                    setSurnames(surnameResponse.data.data.result);
                    setViewMode('surnames');
                } else {
                    setViewMode('none');
                }
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            setViewMode('none');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [appId, userId]);

    const handleUpdateSurnameClick = async () => {
        if (!appId || !userId) return;
        setLoading(true);
        setSearchTerm("");
        setCurrentPage(1);
        setSelectedSurnames([]);
        try {
            const surnameResponse = await axios.post(
                `${BASE_URL}/api/dis_all_surname`,
                {
                    app_id: appId,
                    user_id: userId
                }
            );
            if (surnameResponse.data.success && Array.isArray(surnameResponse.data.data.result)) {
                setSurnames(surnameResponse.data.data.result);
                setViewMode('surnames');
            } else {
                alert("No surnames found.");
            }
        } catch (error) {
            console.error("Error fetching surnames:", error);
            alert("Error fetching surnames.");
        } finally {
            setLoading(false);
        }
    };

    const toggleSurnameSelection = (surname) => {
        const cleanSurname = String(surname).trim();
        setSelectedSurnames(prev =>
            prev.includes(cleanSurname)
                ? prev.filter(s => s !== cleanSurname)
                : [...prev, cleanSurname]
        );
    };

    const handleCreateGroup = async () => {
        if (selectedSurnames.length === 0) return;
        setIsSubmitting(true);
        try {
            const formattedSurnames = selectedSurnames.join(',') + ",";
            const payload = {
                app_id: appId,
                user_id: userId,
                surname_list: formattedSurnames
            };
            const response = await axios.post(`${BASE_URL}/api/insert_surname_group`, payload);
            if (response.data.success) {
                alert("Surname Group Created/Updated Successfully!");
                fetchData();
            } else {
                alert("Failed to create group: " + JSON.stringify(response.data));
            }
        } catch (error) {
            console.error("Error creating surname group:", error);
            alert("Error creating group.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const filteredVoters = voters.filter((voter) => {
        if (!searchTerm) return true;
        const fName = voter.eng_f_name ? voter.eng_f_name.toLowerCase() : "";
        const sName = voter.eng_surname ? voter.eng_surname.toLowerCase() : "";
        const fullName = `${fName} ${sName}`.trim();
        return fullName.includes(searchTerm.toLowerCase());
    });

    const filteredSurnames = surnames.filter((item) => {
        if (!searchTerm) return true;
        const sName = item.eng_surname ? item.eng_surname.toLowerCase() : (item.surname ? item.surname.toLowerCase() : "");
        return sName.includes(searchTerm.toLowerCase());
    });

    const activeList = viewMode === 'voters' ? filteredVoters : filteredSurnames;
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = activeList.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(activeList.length / itemsPerPage);

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxVisibleButtons = 5;
        if (totalPages <= maxVisibleButtons) {
            for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
        } else {
            pageNumbers.push(1);
            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);
            if (currentPage <= 3) endPage = 4;
            if (currentPage >= totalPages - 2) startPage = totalPages - 3;
            if (startPage > 2) pageNumbers.push('...');
            for (let i = startPage; i <= endPage; i++) pageNumbers.push(i);
            if (endPage < totalPages - 1) pageNumbers.push('...');
            pageNumbers.push(totalPages);
        }
        return pageNumbers;
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleWhatsAppShare = async (voter) => {
        if (!voter.contact_no || voter.contact_no === "-") {
            alert("Mobile number not available for this voter.");
            return;
        }
        try {
            const response = await axios.post(`${BASE_URL}/api/get_receipt_in_all_family_for_wtsp`, {
                app_id: appId,
                voter_id: voter.id,
                user_id: appId
            });
            if (response.data.success && response.data.data && response.data.data.result) {
                const familyMembers = response.data.data.result;
                let message = "";
                familyMembers.forEach((fam, index) => {
                    message += `${fam.first_detail.replace(/\s+/g, ' ')}\n`;
                    message += `${fam.full_name}\n`;
                    message += `${fam.middle_name}\n`;
                    message += `${fam.sex_age.replace('स्री', 'स्त्री')}\n`;
                    message += `${fam.idcard_no}\n\n`;
                    message += `मतदान स्थल :\n`;
                    message += `${fam.eng_polling_location}\n\n`;
                    if (index < familyMembers.length - 1) {
                        message += `----------------------------------------\n\n`;
                    }
                });
                const encodedMessage = encodeURIComponent(message);
                let mobile = voter.contact_no.replace(/\D/g, '');
                if (mobile.length === 10) mobile = '91' + mobile;
                window.open(`https://wa.me/${mobile}?text=${encodedMessage}`, '_blank');
            }
        } catch (error) {
            console.error("Error generating WhatsApp slip:", error);
        }
    };

    return (
        <div className='app-container p-4'>

            {/* Header Component Integrated Here */}
            <Header
                title={viewMode === 'voters' ? txt.pageTitleVoters : txt.pageTitleSurnames}
            />

            {/* Filter & Action Bar */}
            {viewMode !== 'none' && !loading && (
                <div className="filter-container mt-3">
                    <div className="search-wrapper w-100" style={{ flex: 1 }}>
                        <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            className="search-input"
                            type="text"
                            placeholder={viewMode === 'voters' ? txt.filterVoters : txt.filterSurnames}
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </div>

                    {viewMode === 'voters' && (
                        <div className='filter-buttons'>
                            <button className="filter-pill active" onClick={handleUpdateSurnameClick} style={{ minWidth: '140px', backgroundColor: '#f59e0b', border: 'none', fontWeight: 'bold' }}>
                                {txt.updateSurnameBtn}
                            </button>
                        </div>
                    )}

                    {viewMode === 'surnames' && selectedSurnames.length > 0 && (
                        <div className='filter-buttons'>
                            <button className="filter-pill active" onClick={handleCreateGroup} disabled={isSubmitting} style={{ minWidth: '140px', backgroundColor: '#22c55e', border: 'none', fontWeight: 'bold' }}>
                                {isSubmitting ? txt.creatingBtn : `${txt.createGroupBtn} (${selectedSurnames.length})`}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Loading Spinner */}
            {loading && (
                <div className="loader-wrapper">
                    <div className="spinner"></div>
                    <p className="loader-text">{txt.loadingData}</p>
                </div>
            )}

            {/* Voters Mode */}
            {!loading && viewMode === 'voters' && (
                <div className="row g-4 pb-5">
                    {currentItems.map((voter, index) => (
                        <div className="col-12 col-md-6 col-lg-4" key={voter.id || index}>
                            <div className="voter-card-clean h-100">
                                <div className="voter-header">
                                    <span className="serial-number">{indexOfFirstItem + index + 1}.</span>
                                    <span className="voter-name-bold ms-2">{voter.eng_f_name} {voter.eng_surname}</span>
                                </div>
                                <div className="voter-body position-relative">
                                    <div className="map-icon-wrapper">
                                        <img src="https://upload.wikimedia.org/wikipedia/commons/a/aa/Google_Maps_icon_%282020%29.svg" alt="Map" width="15" />
                                    </div>
                                    <div className="info-row"><span className="label">{txt.fatherHusband}</span><span className="value">{voter.eng_m_name || "N/A"}</span></div>
                                    <div className="info-row"><span className="label">{txt.serialNo}</span><span className="value">{voter.serial_no || indexOfFirstItem + index + 1}</span></div>
                                    <div className="info-row"><span className="label">{txt.mobile}</span><span className="value mobile-value">{voter.contact_no || "N/A"}</span></div>
                                    <div className="info-row"><span className="label">{txt.idCardNo}</span><span className="value">{voter.idcard_no || "N/A"}</span></div>
                                    <div className="info-row"><span className="label">{txt.boothNo}</span><span className="value">{voter.part_no || "N/A"}</span></div>
                                    <div className="info-row"><span className="label">{txt.houseNo}</span><span className="value">{voter.eng_house_no || "N/A"}</span></div>
                                    <div className="info-row"><span className="label">{txt.pollingLoc}</span><span className="value">{voter.eng_polling_location || "-"}</span></div>
                                </div>
                                <div className="voter-actions">
                                    <ActionButton icon="📞" color="#0ea5e9" label={txt.call} link={`tel:${voter.contact_no}`} />
                                    <ActionButton icon="✉️" color="#f59e0b" label={txt.text} link={`sms:${voter.contact_no}`} />
                                    <button onClick={() => handleWhatsAppShare(voter)} className="action-btn-wrapper" style={{ textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                                        <div className="circle-btn" style={{ backgroundColor: '#22c55e' }}>💬</div>
                                        <span className="btn-label">{txt.slip}</span>
                                    </button>
                                    <ActionButton icon="👤" color="#ef4444" label={txt.prachar} />
                                    {/* Updated Print Button with Navigation */}
                                    <ActionButton
                                        icon="🖨"
                                        color="#f97316"
                                        label={txt.print}
                                        onClick={() => navigate('/printer', { state: { voter_id: voter.id } })}
                                    />
                                    <ActionButton icon="👨‍👩‍👧" color="#fbbf24" label={txt.family} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Surnames Mode */}
            {!loading && viewMode === 'surnames' && (
                <div className="row g-3 pb-5">
                    {currentItems.map((item, index) => {
                        const surnameText = item.eng_surname || item.surname || "Unknown";
                        const isSelected = selectedSurnames.includes(surnameText);
                        return (
                            <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2" key={`surname-${indexOfFirstItem + index}`}>
                                <div className={`surname-card-minimal ${isSelected ? 'selected' : ''}`} onClick={() => toggleSurnameSelection(surnameText)}>
                                    <input type="checkbox" className="surname-checkbox" checked={isSelected} readOnly />
                                    <div className="surname-info-minimal">
                                        <span className="surname-title-minimal">{surnameText}</span>
                                        <span className="surname-count-minimal">Voters: {item.total_voter || item.total || "0"}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Pagination */}
            {!loading && viewMode !== 'none' && activeList.length > itemsPerPage && (
                <div className="pagination-wrapper">
                    <div className="pagination-content">
                        <button className="pagination-nav-btn" onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
                            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7" /></svg>
                        </button>
                        <div className="page-numbers">
                            {getPageNumbers().map((number, index) => (
                                <React.Fragment key={index}>
                                    {number === '...' ? (<span className="page-dots">...</span>) : (
                                        <button className={`page-num-btn ${currentPage === number ? 'active' : ''}`} onClick={() => paginate(number)}>{number}</button>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                        <button className="pagination-nav-btn" onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages}>
                            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg>
                        </button>
                    </div>
                </div>
            )}

            <style jsx>{`
                .loader-wrapper { display: flex; flex-direction: column; justify-content: center; align-items: center; min-height: 60vh; width: 100%; }
                .spinner { width: 50px; height: 50px; border: 4px solid #E5E7EB; border-top: 4px solid #2563EB; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 16px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); }
                .loader-text { color: #6B7280; font-weight: 500; font-size: 1.1rem; animation: pulse 1.5s ease-in-out infinite; }
                @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
                
                .surname-card-minimal { background: white; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 16px; display: flex; align-items: center; gap: 12px; transition: all 0.2s; cursor: pointer; }
                .surname-card-minimal.selected { border-color: #3b82f6; background-color: #f8fafc; box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15); }
                .surname-checkbox { accent-color: #2563eb; pointer-events: none; }
                .surname-info-minimal { display: flex; flex-direction: column; gap: 2px; }
                .surname-title-minimal { font-size: 14px; font-weight: 700; color: #1e293b; text-transform: uppercase; }
                .surname-count-minimal { font-size: 12px; color: #64748b; }
                
                .pagination-wrapper { display: flex; justify-content: center; padding: 20px 0; position: sticky; bottom: 20px; z-index: 50; pointer-events: none; }
                .pagination-content { background: white; border: 1px solid #E2E8F0; border-radius: 50px; padding: 8px 16px; display: flex; align-items: center; gap: 10px; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1); pointer-events: auto; }
                .pagination-nav-btn { display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; border-radius: 50%; background: transparent; border: none; color: #2563EB; cursor: pointer; }
                .page-num-btn { width: 32px; height: 32px; border-radius: 50%; border: 1px solid transparent; background: transparent; color: #475569; font-weight: 600; cursor: pointer; }
                .page-num-btn.active { background: #2563EB; color: white; }
            `}</style>
        </div>
    );
};

// Updated ActionButton to accept onClick props
const ActionButton = ({ icon, color, label, link, onClick }) => {
    if (onClick) {
        return (
            <button onClick={onClick} className="action-btn-wrapper" style={{ textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
                <span className="btn-label">{label}</span>
            </button>
        );
    }
    
    return (
        <a href={link || "#"} className="action-btn-wrapper" style={{ textDecoration: 'none' }} target="_blank" rel="noreferrer">
            <div className="circle-btn" style={{ backgroundColor: color }}>{icon}</div>
            <span className="btn-label">{label}</span>
        </a>
    );
};

export default SurnameGroup;